import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, ChevronDown, ChevronUp } from 'lucide-react';

const ProjectTeam = ({ projectId }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    fetchTeamMembers();
    // Poll for online status every 5 seconds
    const interval = setInterval(fetchTeamMembers, 5000);
    return () => clearInterval(interval);
  }, [projectId]);

  const fetchTeamMembers = async () => {
    try {
      // Get project to find team members
      const projectRes = await axios.get(`${API}/projects/${projectId}`);
      const project = projectRes.data;
      
      // Get all tasks to find assigned users
      const tasksRes = await axios.get(`${API}/projects/${projectId}/tasks`);
      const tasks = tasksRes.data;
      
      // Get all users to check online status
      const usersRes = await axios.get(`${API}/users`);
      const allUsers = usersRes.data;
      
      // Combine owner, team members, and assigned users
      const memberIds = [
        project.owner_id, 
        ...(project.team_member_ids || []),
        ...tasks.map(t => t.assigned_to_id).filter(Boolean)
      ];
      const uniqueIds = [...new Set(memberIds)];
      
      // Map to user objects with online status
      const members = uniqueIds
        .map(id => allUsers.find(u => u.id === id))
        .filter(Boolean)
        .map(user => ({
          ...user,
          isOwner: user.id === project.owner_id
        }));
      
      // Sort: online first, then by name
      members.sort((a, b) => {
        if (a.is_online && !b.is_online) return -1;
        if (!a.is_online && b.is_online) return 1;
        return a.name.localeCompare(b.name);
      });
      
      setTeamMembers(members);
    } catch (error) {
      console.error('Failed to fetch team members', error);
    }
  };

  const onlineCount = teamMembers.filter(m => m.is_online).length;

  return (
    <Card data-testid="project-team-card">
      <CardHeader 
        className="cursor-pointer hover:bg-muted/50 transition-colors py-3"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Team
            <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full">
              {teamMembers.length}
            </span>
            {onlineCount > 0 && (
              <span className="text-xs bg-green-500/10 text-green-600 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                {onlineCount} online
              </span>
            )}
          </div>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </CardTitle>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          <div className="space-y-1">
            {teamMembers.map((member) => (
              <div 
                key={member.id}
                className={`flex items-center gap-2 p-2 rounded-lg ${
                  member.is_online ? 'bg-muted/30' : 'opacity-50'
                }`}
              >
                <div className="relative">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    member.is_online 
                      ? 'bg-primary/10 text-primary' 
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {member.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  {member.is_online && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-background" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm truncate ${member.is_online ? 'font-medium' : 'text-muted-foreground'}`}>
                      {member.name}
                    </span>
                    {member.isOwner && (
                      <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                        Leiter
                      </span>
                    )}
                  </div>
                  <span className={`text-xs ${member.is_online ? 'text-green-600' : 'text-muted-foreground'}`}>
                    {member.is_online ? 'Online' : 'Offline'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default ProjectTeam;
